// ─── Types for Finishing Preview (Supabase-backed) ───

export interface FinishingLine {
  part_code: string;
  part_name?: string;
  finish_code: string;
  finish_name?: string;
  color_system?: 'pantone' | 'custom' | 'none';
  color_code: string;
  note?: string;
  image_url?: string;
}

export interface FinishingPreviewJob {
  id: string;
  customer_id?: string | null;
  user_id?: string | null;
  product_id: string;
  working_image_id: string;
  status: 'queued' | 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
  progress_step: string;
  progress_percent: number;
  progress_message: string;
  structured_lines_json: FinishingLine[];
  additional_request?: string | null;
  source?: string;
  request_locale?: string;
  result_id?: string | null;
  result_image_url?: string | null;
  error_code?: string | null;
  error_message?: string | null;
  created_at: string;
  started_at?: string | null;
  completed_at?: string | null;
  updated_at?: string;
}

export interface FinishingPreviewResult {
  id: string;
  job_id: string;
  product_id: string;
  working_image_id: string;
  image_url: string;
  public_url?: string | null;
  width?: number | null;
  height?: number | null;
  format?: string;
  result_status: string;
}

export interface CreateJobRequest {
  product_id: string;
  working_image_id: string;
  structured_lines: FinishingLine[];
  additional_request?: string;
  source?: string;
  request_locale?: string;
}

// ─── Prompt generation helpers (summary only, NOT sent to Kling) ───

function extractEnglishName(koreanWithEnglish: string): string {
  const match = koreanWithEnglish.match(/\(([^)]+)\)/);
  return match ? match[1].trim() : koreanWithEnglish.trim();
}

export interface SummaryInput {
  partName: string;
  methodName: string;
  colorName: string;
}

export function generateSummaryText(selections: SummaryInput[]): string {
  if (selections.length === 0) return '';
  const parts = selections.map((s) => {
    const part = extractEnglishName(s.partName);
    const method = extractEnglishName(s.methodName);
    return `${part}에 ${s.colorName} ${method} 적용`;
  });
  return parts.join(', ') + '합니다.';
}

export function buildFinishingLinesFromSelections(
  selections: SummaryInput[],
): FinishingLine[] {
  return selections.map((s) => {
    const partEn = extractEnglishName(s.partName);
    const partCode = partEn.toLowerCase().replace(/\s+/g, '_');
    const methodEn = extractEnglishName(s.methodName);
    return {
      part_code: partCode,
      part_name: partEn,
      finish_code: methodEn.toLowerCase().replace(/\s+/g, '_'),
      finish_name: methodEn,
      color_system: 'pantone' as const,
      color_code: s.colorName,
      note: '',
    };
  });
}

// ─── Progress step display mappings ───

export const PROGRESS_STEP_LABELS: Record<string, string> = {
  queued: 'Preview job created.',
  pending: 'Preview request submitted.',
  picked_by_worker: 'Worker picked up the job.',
  validating_request: 'Validating request.',
  building_prompt: 'Building AI prompt.',
  submitting_to_ai: 'Submitting to AI service.',
  ai_processing: 'AI is generating the image.',
  downloading_result: 'Downloading result image.',
  uploading_to_storage: 'Uploading result image.',
  saving_result: 'Saving result.',
  completed: 'Preview completed.',
  failed: 'Preview failed.',
};

export const PROGRESS_STEP_PERCENT: Record<string, number> = {
  queued: 0,
  pending: 0,
  picked_by_worker: 10,
  validating_request: 20,
  building_prompt: 30,
  submitting_to_ai: 40,
  ai_processing: 60,
  downloading_result: 80,
  uploading_to_storage: 85,
  saving_result: 95,
  completed: 100,
  failed: 0,
};

export const ALL_PROGRESS_STEPS = [
  'queued',
  'pending',
  'picked_by_worker',
  'validating_request',
  'building_prompt',
  'submitting_to_ai',
  'ai_processing',
  'downloading_result',
  'uploading_to_storage',
  'saving_result',
  'completed',
];